import { useState, useEffect, useMemo } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  ChevronLeft, ChevronRight, Headset, CheckCircle, AlertTriangle, Clock, ArrowLeftRight, Bug, Wand2,
} from 'lucide-react'
import { startOfWeek } from 'date-fns'
import { apiClient } from '@/lib/api.ts'
import {
  Button,
  Card,
  ConfirmDialog,
  EmptyState,
  ErrorState,
  LoadingState,
  Modal,
  PageHeader,
  Select,
} from '@/components/ui'
import { useToast } from '@/components/ui'
import { DispositionCalendar } from './components/DispositionCalendar'

// ─── Types ────────────────────────────────────────────────────────────────────

interface Employee {
  id: number
  name: string
}

interface ShiftAssignment {
  vacationRequestId: number
  employeeId: number
  employeeName: string | null
  startTime: string
  endTime: string
  status: 'proposal' | 'confirmed'
}

interface ShiftSlot {
  ruleId: number
  date: string
  dayOfWeek: number
  startTime: string
  endTime: string
  minimumEmployees: number
  maximumEmployees: number
  status: 'proposal' | 'confirmed' | 'empty'
  assignments: ShiftAssignment[]
}

interface DefinitionWeekView {
  id: number
  name: string
  confirmedPlanningAdditionalId: number | null
  shiftRules: Array<{
    id: number
    weekdays: string[]
    startTime: string
    endTime: string
    minimumEmployees: number
    maximumEmployees: number
  }>
  shifts: ShiftSlot[]
}

interface ServiceDeskWeekResult {
  weekStart: string
  definitions: DefinitionWeekView[]
  tanssError: string | null
  syncResult: unknown
  debugLog: string[]
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

function currentMonday(): string {
  const now = new Date()
  const day = now.getUTCDay()
  const diff = day === 0 ? -6 : 1 - day
  const mon = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate() + diff))
  return mon.toISOString().split('T')[0]!
}

function addWeeks(weekStart: string, delta: number): string {
  const d = new Date(weekStart + 'T00:00:00Z')
  d.setUTCDate(d.getUTCDate() + delta * 7)
  return d.toISOString().split('T')[0]!
}

function isoWeekNum(weekStart: string): number {
  const d = new Date(weekStart + 'T00:00:00Z')
  const jan4 = new Date(Date.UTC(d.getUTCFullYear(), 0, 4))
  const dow = jan4.getUTCDay() || 7
  const w1 = new Date(jan4)
  w1.setUTCDate(jan4.getUTCDate() - dow + 1)
  return Math.ceil(((d.getTime() - w1.getTime()) / 86400000 + 1) / 7)
}

function formatWeekLabel(weekStart: string): string {
  const mon = new Date(weekStart + 'T00:00:00Z')
  const fri = new Date(weekStart + 'T00:00:00Z')
  fri.setUTCDate(fri.getUTCDate() + 4)
  const kw = isoWeekNum(weekStart)
  const fmt = (d: Date) =>
    d.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', timeZone: 'UTC' })
  const fmtFull = (d: Date) =>
    d.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric', timeZone: 'UTC' })
  return `KW ${String(kw).padStart(2, '0')} · ${fmt(mon)} – ${fmtFull(fri)}`
}

function isPastWeek(weekStart: string): boolean {
  const fri = new Date(weekStart + 'T00:00:00Z')
  fri.setUTCDate(fri.getUTCDate() + 4)
  return fri.toISOString().substring(0, 10) < new Date().toISOString().substring(0, 10)
}

function formatShortDate(dateStr: string): string {
  return new Date(dateStr + 'T00:00:00Z').toLocaleDateString('de-DE', {
    weekday: 'short', day: '2-digit', month: '2-digit', timeZone: 'UTC',
  })
}

function extractErrorMessage(err: unknown): string {
  return (
    (err as { response?: { data?: { message?: string } } })?.response?.data?.message
    ?? (err instanceof Error ? err.message : 'Unbekannter Fehler')
  )
}

// ─── Technician Swap Modal ────────────────────────────────────────────────────

function TechnicianSwapModal({
  open,
  onClose,
  onConfirm,
  employees,
  loading,
  error,
}: {
  open: boolean
  onClose: () => void
  onConfirm: (technicianId: number) => void
  employees: Employee[]
  loading: boolean
  error: string | null
}) {
  const [selectedId, setSelectedId] = useState<string>('__none__')

  useEffect(() => {
    if (open) setSelectedId('__none__')
  }, [open])

  const handleConfirm = () => {
    const id = Number(selectedId)
    if (id > 0) onConfirm(id)
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Techniker austauschen"
      size="sm"
      footer={
        <>
          <Button variant="secondary" size="sm" onClick={onClose} disabled={loading}>
            Abbrechen
          </Button>
          <Button
            variant="primary"
            size="sm"
            onClick={handleConfirm}
            disabled={selectedId === '__none__' || loading}
            loading={loading}
          >
            Austauschen
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        <Select
          label="Neuer Techniker"
          value={selectedId}
          onValueChange={setSelectedId}
          options={[
            { value: '__none__', label: 'Techniker wählen ...' },
            ...employees.map((e) => ({ value: String(e.id), label: e.name })),
          ]}
          disabled={loading}
        />
        {error && (
          <p className="text-sm text-red-600 bg-red-50 border border-red-100 rounded p-2">{error}</p>
        )}
      </div>
    </Modal>
  )
}

// ─── Shift Block (compact row) ────────────────────────────────────────────────

function ShiftBlock({
  slot,
  definitionId,
  isPast,
  onAccept,
  onSwap,
  accepting,
  timelineError,
}: {
  slot: ShiftSlot
  definitionId: number
  isPast: boolean
  onAccept: (slot: ShiftSlot, definitionId: number) => void
  onSwap: (vrId: number) => void
  accepting: boolean
  timelineError: boolean
}) {
  const hasProposals = slot.status === 'proposal'
  const isUnderStaffed = slot.assignments.length < slot.minimumEmployees

  return (
    <div className={`flex items-center gap-2 py-1 ${isUnderStaffed && slot.assignments.length > 0 ? 'bg-amber-50' : ''}`}>
      {/* Time range */}
      <span className="text-xs font-semibold text-gray-700 w-[88px] shrink-0 tabular-nums">
        {slot.startTime}–{slot.endTime}
      </span>

      {/* Assignments inline */}
      <div className="flex-1 flex items-center gap-2 flex-wrap min-w-0">
        {slot.assignments.length === 0 ? (
          timelineError ? (
            <span className="inline-flex items-center gap-1 text-xs text-red-500">
              <AlertTriangle className="w-3 h-3 shrink-0" />
              Fehler
            </span>
          ) : (
            <span className="text-xs text-gray-400 italic">Keine Besetzung</span>
          )
        ) : (
          slot.assignments.map((a) => (
            <span key={a.vacationRequestId} className="inline-flex items-center gap-1 min-w-0">
              {a.status === 'confirmed'
                ? <CheckCircle className="w-3 h-3 text-green-500 shrink-0" />
                : <Clock className="w-3 h-3 text-amber-400 shrink-0" />}
              <span className="text-xs text-gray-800 truncate">
                {a.employeeName ?? `#${a.employeeId}`}
              </span>
              {a.status === 'proposal' && !isPast && (
                <Button
                  variant="ghost"
                  size="xs"
                  onClick={() => onSwap(a.vacationRequestId)}
                  disabled={accepting}
                  title="Austauschen"
                >
                  <ArrowLeftRight className="w-3 h-3" />
                </Button>
              )}
            </span>
          ))
        )}
      </div>

      {/* Staffing count */}
      <span className={`text-xs tabular-nums shrink-0 ${
        isUnderStaffed && slot.assignments.length > 0 ? 'text-amber-600 font-medium' : 'text-gray-400'
      }`}>
        {slot.assignments.length}/{slot.maximumEmployees}
        {isUnderStaffed && slot.assignments.length > 0 ? ' ⚠' : ''}
      </span>

      {/* Accept */}
      {hasProposals && !isPast && (
        <Button
          variant="secondary"
          size="xs"
          onClick={() => onAccept(slot, definitionId)}
          loading={accepting}
          disabled={accepting}
          title="Schicht übernehmen"
        >
          <CheckCircle className="w-3 h-3" />
        </Button>
      )}

      {/* Confirmed marker */}
      {slot.status === 'confirmed' && (
        <span className="text-xs text-green-600 font-medium shrink-0">✓</span>
      )}
    </div>
  )
}

// ─── Definition Section (compact) ────────────────────────────────────────────

function DefinitionSection({
  def,
  isPast,
  onAccept,
  onSwap,
  acceptingKey,
  timelineError,
}: {
  def: DefinitionWeekView
  isPast: boolean
  onAccept: (slot: ShiftSlot, definitionId: number) => void
  onSwap: (vrId: number) => void
  acceptingKey: string | null
  timelineError: boolean
}) {
  const shiftsByDate = useMemo(() => {
    const map = new Map<string, ShiftSlot[]>()
    for (const shift of def.shifts) {
      if (!map.has(shift.date)) map.set(shift.date, [])
      map.get(shift.date)!.push(shift)
    }
    return map
  }, [def.shifts])

  const dates = useMemo(() => Array.from(shiftsByDate.keys()).sort(), [shiftsByDate])

  return (
    <Card>
      <Card.Header>
        <h3 className="text-sm font-semibold text-gray-900">{def.name}</h3>
      </Card.Header>
      <Card.Body className="py-1 space-y-0">
        {dates.length === 0 ? (
          <p className="text-xs text-gray-400 py-1">Keine Schichten für diese Woche</p>
        ) : (
          <div className="divide-y divide-gray-100">
            {dates.map((date: string) => {
              const shifts = shiftsByDate.get(date) ?? []
              return (
                <div key={date} className="flex items-start gap-3 py-1">
                  {/* Compact date label */}
                  <span className="text-xs font-medium text-gray-500 w-20 shrink-0 pt-0.5">
                    {formatShortDate(date)}
                  </span>
                  {/* Shifts for this day */}
                  <div className="flex-1 divide-y divide-gray-50">
                    {shifts.map((slot: ShiftSlot) => {
                      const key = `${def.id}:${slot.ruleId}:${slot.date}`
                      return (
                        <ShiftBlock
                          key={key}
                          slot={slot}
                          definitionId={def.id}
                          isPast={isPast}
                          onAccept={onAccept}
                          onSwap={onSwap}
                          accepting={acceptingKey === key}
                          timelineError={timelineError}
                        />
                      )
                    })}
                  </div>
                </div>
              )
            })}
          </div>
        )}
      </Card.Body>
    </Card>
  )
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export function DispoServiceDeskPage() {
  const queryClient = useQueryClient()
  const { toast } = useToast()
  const [weekStart, setWeekStart] = useState(() => currentMonday())

  const [confirmPending, setConfirmPending] = useState<{
    definitionId: number
    ruleId: number
    date: string
    count: number
    min: number
  } | null>(null)

  const [swapVrId, setSwapVrId] = useState<number | null>(null)
  const [swapError, setSwapError] = useState<string | null>(null)
  const [acceptingKey, setAcceptingKey] = useState<string | null>(null)

  const calendarFrom = useMemo(
    () => startOfWeek(new Date(weekStart + 'T00:00:00Z'), { weekStartsOn: 1 }),
    [weekStart],
  )

  const past = isPastWeek(weekStart)

  const weekQuery = useQuery<ServiceDeskWeekResult>({
    queryKey: ['dispo-service-desk-week', weekStart],
    queryFn: () =>
      apiClient.get('/dispo/service-desk', { params: { weekStart } }).then((r) => r.data),
    retry: false,
  })

  const employeesQuery = useQuery<Employee[]>({
    queryKey: ['dispo-service-desk-employees'],
    queryFn: () => apiClient.get('/dispo/service-desk/employees').then((r) => r.data),
    staleTime: 10 * 60 * 1000,
    retry: false,
  })

  const acceptShiftMutation = useMutation({
    mutationFn: ({ definitionId, ruleId, date }: { definitionId: number; ruleId: number; date: string }) =>
      apiClient.post('/dispo/service-desk/accept', { definitionId, ruleId, date }),
    onSuccess: () => {
      setAcceptingKey(null)
      queryClient.invalidateQueries({ queryKey: ['dispo-service-desk-week', weekStart] })
      toast({ title: 'Schicht übernommen', variant: 'default' })
    },
    onError: (err) => {
      setAcceptingKey(null)
      toast({ title: 'Fehler beim Übernehmen', description: extractErrorMessage(err), variant: 'destructive' })
    },
  })

  const acceptAllMutation = useMutation({
    mutationFn: () => apiClient.post('/dispo/service-desk/accept-all', { weekStart }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['dispo-service-desk-week', weekStart] })
      toast({ title: 'Alle Schichten übernommen', variant: 'default' })
    },
    onError: (err) => {
      toast({ title: 'Fehler beim Übernehmen', description: extractErrorMessage(err), variant: 'destructive' })
    },
  })

  const changeRequesterMutation = useMutation({
    mutationFn: ({ vrId, technicianId }: { vrId: number; technicianId: number }) =>
      apiClient.put(`/dispo/service-desk/${vrId}/requester`, { technicianId, weekStart }),
    onSuccess: () => {
      setSwapVrId(null)
      setSwapError(null)
      queryClient.invalidateQueries({ queryKey: ['dispo-service-desk-week', weekStart] })
      toast({ title: 'Techniker ausgetauscht', variant: 'default' })
    },
    onError: (err) => {
      setSwapError(extractErrorMessage(err))
    },
  })

  const hasAnyProposals =
    weekQuery.data?.definitions.some((def) => def.shifts.some((s) => s.status === 'proposal')) ??
    false

  const hasAnyAssignments =
    weekQuery.data?.definitions.some((def) => def.shifts.some((s) => s.assignments.length > 0)) ??
    false

  const proposeMutation = useMutation({
    mutationFn: () => apiClient.post('/dispo/service-desk/propose', { weekStart }),
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: ['dispo-service-desk-week', weekStart] })
      const data = res.data as { alreadyPlanned?: boolean; sync?: { created: number } }
      if (data.alreadyPlanned) {
        toast({ title: 'Woche bereits geplant', description: 'Für diese Woche gibt es bereits Vorschläge oder bestätigte Einträge.', variant: 'default' })
      } else {
        toast({ title: `${data.sync?.created ?? 0} Vorschläge erstellt`, variant: 'default' })
      }
    },
    onError: (err) => {
      toast({ title: 'Belegungsvorschlag fehlgeschlagen', description: extractErrorMessage(err), variant: 'destructive' })
    },
  })

  const doAccept = (definitionId: number, ruleId: number, date: string) => {
    const key = `${definitionId}:${ruleId}:${date}`
    setAcceptingKey(key)
    acceptShiftMutation.mutate({ definitionId, ruleId, date })
  }

  const handleAccept = (slot: ShiftSlot, definitionId: number) => {
    const isUnderStaffed = slot.assignments.length < slot.minimumEmployees
    if (isUnderStaffed) {
      setConfirmPending({
        definitionId,
        ruleId: slot.ruleId,
        date: slot.date,
        count: slot.assignments.length,
        min: slot.minimumEmployees,
      })
    } else {
      doAccept(definitionId, slot.ruleId, slot.date)
    }
  }

  const handleSwap = (vrId: number) => {
    setSwapError(null)
    setSwapVrId(vrId)
  }

  const weekLabel = formatWeekLabel(weekStart)

  return (
    <div className="space-y-6">
      <PageHeader
        title={
          <span className="flex items-center gap-2">
            <Headset className="w-6 h-6 text-blue-500" />
            Service-Desk Planung
          </span>
        }
        description="Wöchentliche Schichtzuweisung basierend auf dynamischen Dienstdefinitionen."
        actions={
          <div className="flex items-center gap-2">
            <Button
              variant="secondary"
              size="sm"
              onClick={() => setWeekStart((w: string) => addWeeks(w, -1))}
              aria-label="Vorherige Woche"
            >
              <ChevronLeft className="w-4 h-4" />
            </Button>
            <span className="text-body font-medium text-gray-800 min-w-[260px] text-center">
              {weekLabel}
            </span>
            <Button
              variant="secondary"
              size="sm"
              onClick={() => setWeekStart((w: string) => addWeeks(w, 1))}
              aria-label="Nächste Woche"
            >
              <ChevronRight className="w-4 h-4" />
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setWeekStart(currentMonday())}
            >
              Heute
            </Button>
            {!past && (
              <Button
                variant="secondary"
                size="sm"
                onClick={() => proposeMutation.mutate()}
                disabled={proposeMutation.isPending || hasAnyAssignments || weekQuery.isLoading}
                loading={proposeMutation.isPending}
                title="Vorschläge für diese Woche automatisch generieren (nur wenn noch keine Einträge vorhanden)"
              >
                <Wand2 className="w-3.5 h-3.5 mr-1" />
                Belegung vorschlagen
              </Button>
            )}
            {!past && (
              <Button
                variant="secondary"
                size="sm"
                onClick={() => acceptAllMutation.mutate()}
                disabled={acceptAllMutation.isPending || !hasAnyProposals || weekQuery.isLoading}
                loading={acceptAllMutation.isPending}
              >
                <CheckCircle className="w-3.5 h-3.5 mr-1" />
                Alle übernehmen
              </Button>
            )}
          </div>
        }
      />

      {/* Past week read-only banner */}
      {past && (
        <Card>
          <Card.Body>
            <p className="text-sm text-gray-500 text-center">
              Vergangene Woche — keine Änderungen möglich
            </p>
          </Card.Body>
        </Card>
      )}

      {/* TANSS error */}
      {weekQuery.data?.tanssError && (
        <Card>
          <Card.Body>
            <div className="flex items-start gap-3 text-amber-700">
              <AlertTriangle className="w-5 h-5 shrink-0 mt-0.5" />
              <div>
                <p className="text-body font-medium">TANSS nicht erreichbar</p>
                <p className="text-caption text-amber-600 mt-1">{weekQuery.data.tanssError}</p>
              </div>
            </div>
          </Card.Body>
        </Card>
      )}

      {/* Disposition calendar */}
      <DispositionCalendar key={weekStart} from={calendarFrom} mode="week" showHeader showEmployeeImages />

      {/* Loading */}
      {weekQuery.isLoading && (
        <Card>
          <Card.Body>
            <LoadingState />
          </Card.Body>
        </Card>
      )}

      {/* API error */}
      {weekQuery.isError && (
        <Card>
          <Card.Body>
            <ErrorState
              title="Wochenplan konnte nicht geladen werden"
              description={weekQuery.error instanceof Error ? weekQuery.error.message : 'Unbekannter Fehler'}
            />
          </Card.Body>
        </Card>
      )}

      {/* No definitions */}
      {weekQuery.data && !weekQuery.isLoading && weekQuery.data.definitions.length === 0 && (
        <Card>
          <Card.Body>
            <EmptyState
              icon={<Headset className="w-8 h-8" />}
              title="Keine Dienstdefinitionen konfiguriert"
              description="Erstelle Dienstdefinitionen in den Einstellungen, um die Planung zu starten."
            />
          </Card.Body>
        </Card>
      )}

      {/* TANSS Debug Log */}
      {weekQuery.data && weekQuery.data.debugLog && weekQuery.data.debugLog.length > 0 && (
        <details className="group">
          <summary className="flex items-center gap-2 cursor-pointer select-none text-sm text-gray-500 hover:text-gray-700 p-2 rounded border border-dashed border-gray-300 hover:border-gray-400">
            <Bug className="w-4 h-4 text-gray-400 group-open:text-blue-500" />
            <span className="font-medium">TANSS Debug-Log</span>
            <span className="text-xs text-gray-400 ml-1">({weekQuery.data.debugLog.length} Einträge)</span>
          </summary>
          <div className="mt-2 rounded border border-gray-200 bg-gray-900 p-3 overflow-x-auto">
            <pre className="text-xs text-green-300 font-mono whitespace-pre-wrap leading-5">
              {weekQuery.data.debugLog.join('\n')}
            </pre>
          </div>
        </details>
      )}

      {/* One section per definition – compact list */}
      {weekQuery.data && weekQuery.data.definitions.length > 0 && (
        <div className="space-y-3">
          {weekQuery.data.definitions.map((def) => (
            <DefinitionSection
              key={def.id}
              def={def}
              isPast={past}
              onAccept={handleAccept}
              onSwap={handleSwap}
              acceptingKey={acceptingKey}
              timelineError={!!weekQuery.data?.tanssError}
            />
          ))}
        </div>
      )}

      {/* Confirm dialog for understaffed shift */}
      <ConfirmDialog
        open={confirmPending !== null}
        title="Unterbesetzte Schicht übernehmen"
        variant="warning"
        message={
          confirmPending ? (
            <p className="text-sm text-gray-700">
              Diese Schicht ist unterbesetzt ({confirmPending.count} von mindestens{' '}
              {confirmPending.min} Personen). Trotzdem übernehmen?
            </p>
          ) : null
        }
        confirmLabel="Trotzdem übernehmen"
        cancelLabel="Abbrechen"
        loading={acceptShiftMutation.isPending}
        onConfirm={() => {
          if (confirmPending) {
            doAccept(confirmPending.definitionId, confirmPending.ruleId, confirmPending.date)
            setConfirmPending(null)
          }
        }}
        onCancel={() => setConfirmPending(null)}
      />

      {/* Technician swap modal */}
      <TechnicianSwapModal
        open={swapVrId !== null}
        onClose={() => {
          setSwapVrId(null)
          setSwapError(null)
        }}
        onConfirm={(technicianId) => {
          if (swapVrId !== null) {
            changeRequesterMutation.mutate({ vrId: swapVrId, technicianId })
          }
        }}
        employees={employeesQuery.data ?? []}
        loading={changeRequesterMutation.isPending}
        error={swapError}
      />
    </div>
  )
}
